import React from "react";

type TrackerItem = {
  message?: string;
  Date?: string;
  updatedDate?: string;
  changedBy?: string;
  createdby?: string;
  employeeName?: string;
  employeePhone?: string;
};
type StatusTrackerInput = string | string[] | TrackerItem[];
const isStringArray = (value: unknown[]): value is string[] =>
  value.every((item) => typeof item === "string");

const isTrackerItemArray = (value: unknown[]): value is TrackerItem[] =>
  value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      ("message" in item || "Date" in item)
  );

const FormatStatusTrackerData = ({
  trackingData,
}: {
  trackingData: StatusTrackerInput;
}) => {
  let data: TrackerItem[] = [];

  if (typeof trackingData === "string") {
    try {
      data = JSON.parse(trackingData) as TrackerItem[];
    } catch (error) {
      console.error("Error parsing trackingData:", error);
    }
  } else if (Array.isArray(trackingData)) {
    if (isStringArray(trackingData)) {
      data = trackingData
        .map((item) => {
          try {
            return JSON.parse(item) as TrackerItem;
          } catch {
            return null;
          }
        })
        .filter((item): item is TrackerItem => item !== null);
    } else if (isTrackerItemArray(trackingData)) {
      data = trackingData;
    }
  }

  const styles = {
    trackerContainer: {
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      padding: "10px",
      border: "1px solid #e0e0e0",
      borderRadius: "5px",
      backgroundColor: "#f9f9f9",
      width: "100%",
      margin: "0 auto",
      marginBottom: "16px",
    } as React.CSSProperties,
    trackerItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      marginBottom: "16px",
      padding: "8px",
      borderRadius: "5px",
      backgroundColor: "#ffffff",
      border: "1px solid #e0e0e0",
      height: "auto",
    } as React.CSSProperties,
    dot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: "gray",
      marginRight: "10px",
      flexShrink: 0,
    } as React.CSSProperties,
    latestDot: {
      backgroundColor: "green",
    } as React.CSSProperties,
    messageContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
    } as React.CSSProperties,
    messageText: {
      fontWeight: 500,
      fontSize: "14px",
      color: "#000",
      marginRight: "10px",
      textAlign: "center",
    } as React.CSSProperties,
    detailsText: {
      fontSize: "12px",
      color: "gray",
      textAlign: "center",
    } as React.CSSProperties,
    noDataText: {
      color: "red",
      fontStyle: "italic",
      textAlign: "center",
      margin: "20px 0",
    } as React.CSSProperties,
  };

  return (
    <div style={styles.trackerContainer}>
      {data.length > 0 ? (
        [...data].reverse().map((item, index) => {
          const uniqueKey = `${item?.Date ?? item?.updatedDate ?? index}-${item?.changedBy ?? item?.createdby ?? ""}-${item?.message ?? ""}-${index}`;

          return (
            <div key={uniqueKey} style={styles.trackerItem}>
              <div
                style={{
                  ...styles.dot,
                  ...(index === 0 ? styles.latestDot : {}),
                }}
              />
              <div style={styles.messageContainer}>
                <span style={styles.messageText}>{item?.message}</span>
                <span style={styles.detailsText}>
                  {[
                    item?.Date ?? item?.updatedDate ?? "",
                    // Show "By:" only when there is no engineer (e.g. customer created, admin assigned)
                    !item?.employeeName &&
                    (item?.changedBy ?? item?.createdby)
                      ? `By: ${item?.changedBy ?? item?.createdby}`
                      : "",
                    item?.employeeName ? `Engineer: ${item.employeeName}` : "",
                    item?.employeePhone ? `Phone: ${item.employeePhone}` : "",
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                </span>
              </div>
            </div>
          );
        })
      ) : (
        <span style={styles.noDataText}>No data available.</span>
      )}
    </div>
  );
};

export default FormatStatusTrackerData;
